import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Calendar, MessageCircle, Send, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        appointmentType: '',
        preferredDate: '',
        preferredTime: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        setIsSubmitting(true);
        
        // Simulate form submission
        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitStatus('success');
            setFormData({
                name: '',
                email: '',
                phone: '',
                appointmentType: '',
                preferredDate: '',
                preferredTime: '',
                message: ''
            });
            
            // Clear success message after 5 seconds
            setTimeout(() => {
                setSubmitStatus(null);
            }, 5000);
        }, 2000);
    };

    const contactMethods = [
        {
            icon: Phone,
            title: 'Call Us',
            primary: '(555) 123-CARE',
            secondary: '24/7 Emergency Line',
            description: 'Speak directly with our medical staff',
            color: 'from-green-500 to-green-600'
        },
        {
            icon: Mail,
            title: 'Email Us',
            primary: 'info@healthcareportal.com',
            secondary: 'appointments@healthcareportal.com',
            description: 'Send us a message anytime',
            color: 'from-blue-500 to-blue-600'
        },
        {
            icon: MessageCircle,
            title: 'Live Chat',
            primary: 'Available 9 AM - 6 PM',
            secondary: 'Monday - Friday',
            description: 'Chat with our support team',
            color: 'from-purple-500 to-purple-600'
        },
        {
            icon: Calendar,
            title: 'Online Booking',
            primary: 'Schedule 24/7',
            secondary: 'Instant Confirmation',
            description: 'Book appointments online',
            color: 'from-orange-500 to-orange-600'
        }
    ];

    const officeHours = [
        { day: 'Monday - Friday', hours: '8:00 AM - 6:00 PM' },
        { day: 'Saturday', hours: '9:00 AM - 4:00 PM' },
        { day: 'Sunday', hours: '10:00 AM - 2:00 PM' },
        { day: 'Emergency', hours: '24/7 Available' }
    ];

    const departments = [
        'General Medicine',
        'Cardiology',
        'Pediatrics',
        'Dermatology',
        'Orthopedics',
        'Gynecology',
        'Emergency Care',
        'Mental Health'
    ];

    return (
        <div className='min-h-screen bg-gradient-to-br from-purple-50 to-blue-50'>
            {/* Hero Section */}
            <div className='relative bg-gradient-to-r from-purple-900 via-purple-800 to-blue-900 text-white py-20'>
                <div className='absolute inset-0 bg-black opacity-20'></div>
                <div className='relative max-w-7xl mx-auto px-4 text-center'>
                    <h1 className='text-5xl md:text-6xl font-bold mb-6'>
                        Contact Us
                    </h1>
                    <p className='text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed'>
                        We're here to help with all your healthcare needs. Reach out to us today.
                    </p>
                    <div className='inline-flex items-center space-x-2 bg-white bg-opacity-20 px-6 py-3 rounded-full backdrop-blur-sm'>
                        <Phone className='w-6 h-6' />
                        <span className='text-lg font-semibold'>(555) 123-CARE</span>
                    </div>
                </div>
            </div>

            {/* Quick Contact Methods */}
            <div className='py-16 bg-white'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='text-center mb-12'>
                        <h2 className='text-4xl font-bold text-purple-900 mb-4'>Get In Touch</h2>
                        <p className='text-xl text-gray-600'>Choose the most convenient way to reach us</p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
                        {contactMethods.map((method, index) => {
                            const Icon = method.icon;
                            return (
                                <div key={index} className='bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:translateY-2 cursor-pointer group'>
                                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${method.color} rounded-full mb-4 group-hover:shadow-lg transition-shadow`}>
                                        <Icon className='w-8 h-8 text-white' />
                                    </div>
                                    <h3 className='text-xl font-bold text-purple-900 mb-2'>{method.title}</h3>
                                    <p className='text-purple-700 font-semibold mb-1'>{method.primary}</p>
                                    <p className='text-gray-600 text-sm mb-3'>{method.secondary}</p>
                                    <p className='text-gray-500 text-sm'>{method.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Contact Form and Info */}
            <div className='py-20 bg-gradient-to-r from-purple-100 to-blue-100'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
                        {/* Contact Form */}
                        <div className='bg-white rounded-2xl shadow-xl p-8'>
                            <h2 className='text-3xl font-bold text-purple-900 mb-6'>Schedule an Appointment</h2>
                            
                            {submitStatus === 'success' && (
                                <div className='bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center space-x-3'>
                                    <CheckCircle className='w-6 h-6 text-green-600' />
                                    <div>
                                        <p className='text-green-800 font-semibold'>Request Submitted Successfully!</p>
                                        <p className='text-green-600 text-sm'>We'll contact you within 24 hours to confirm your appointment.</p>
                                    </div>
                                </div>
                            )}

                            <div className='space-y-6'>
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Full Name</label>
                                        <input
                                            type='text'
                                            name='name'
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            placeholder='Enter your full name'
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Email Address</label>
                                        <input
                                            type='email'
                                            name='email'
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            placeholder='Enter your email'
                                            required
                                        />
                                    </div>
                                </div>

                                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Phone Number</label>
                                        <input
                                            type='tel'
                                            name='phone'
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            placeholder='(555) 123-4567'
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Appointment Type</label>
                                        <select
                                            name='appointmentType'
                                            value={formData.appointmentType}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            required
                                        >
                                            <option value=''>Select Department</option>
                                            {departments.map((dept, index) => (
                                                <option key={index} value={dept}>{dept}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Preferred Date</label>
                                        <input
                                            type='date'
                                            name='preferredDate'
                                            value={formData.preferredDate}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Preferred Time</label>
                                        <select
                                            name='preferredTime'
                                            value={formData.preferredTime}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            required
                                        >
                                            <option value=''>Select Time</option>
                                            <option value='9:00 AM'>9:00 AM</option>
                                            <option value='10:00 AM'>10:00 AM</option>
                                            <option value='11:00 AM'>11:00 AM</option>
                                            <option value='1:00 PM'>1:00 PM</option>
                                            <option value='2:00 PM'>2:00 PM</option>
                                            <option value='3:00 PM'>3:00 PM</option>
                                            <option value='4:00 PM'>4:00 PM</option>
                                            <option value='5:00 PM'>5:00 PM</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className='block text-gray-700 font-semibold mb-2'>Message (Optional)</label>
                                    <textarea
                                        name='message'
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        rows='4'
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none'
                                        placeholder='Please describe your symptoms or reason for visit...'
                                    ></textarea>
                                </div>

                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className='w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed'
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                                            <span>Submitting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Send className='w-5 h-5' />
                                            <span>Request Appointment</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className='space-y-8'>
                            {/* Location */}
                            <div className='bg-white rounded-2xl shadow-xl p-8'>
                                <div className='flex items-center space-x-3 mb-6'>
                                    <div className='w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center'>
                                        <MapPin className='w-6 h-6 text-white' />
                                    </div>
                                    <h3 className='text-2xl font-bold text-purple-900'>Our Location</h3>
                                </div>
                                <div className='space-y-4'>
                                    <p className='text-gray-700'>
                                        <strong>Healthcare Medical Center</strong><br />
                                        123 Medical Center Drive<br />
                                        Healthcare City, HC 12345<br />
                                        United States
                                    </p>
                                    <div className='bg-gray-100 rounded-lg p-4'>
                                        <div className='aspect-video bg-gray-200 rounded-lg flex items-center justify-center'>
                                            <div className='text-gray-500 text-center'>
                                                <MapPin className='w-12 h-12 mx-auto mb-2' />
                                                <p>Interactive Map</p>
                                                <p className='text-sm'>Click to view directions</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Office Hours */}
                            <div className='bg-white rounded-2xl shadow-xl p-8'>
                                <div className='flex items-center space-x-3 mb-6'>
                                    <div className='w-12 h-12 bg-gradient-to-r from-green-600 to-green-700 rounded-lg flex items-center justify-center'>
                                        <Clock className='w-6 h-6 text-white' />
                                    </div>
                                    <h3 className='text-2xl font-bold text-purple-900'>Office Hours</h3>
                                </div>
                                <div className='space-y-3'>
                                    {officeHours.map((schedule, index) => (
                                        <div key={index} className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
                                            <span className='font-semibold text-gray-700'>{schedule.day}</span>
                                            <span className='text-purple-600 font-medium'>{schedule.hours}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className='mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3'>
                                    <AlertCircle className='w-6 h-6 text-red-600 flex-shrink-0 mt-0.5' />
                                    <div>
                                        <p className='text-red-800 font-semibold'>Emergency Care</p>
                                        <p className='text-red-600 text-sm'>For medical emergencies, call 911 or visit our emergency department available 24/7.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className='py-20 bg-white'>
                <div className='max-w-4xl mx-auto px-4'>
                    <div className='text-center mb-12'>
                        <h2 className='text-4xl font-bold text-purple-900 mb-4'>Frequently Asked Questions</h2>
                        <p className='text-xl text-gray-600'>Quick answers to common questions</p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
                        <div className='bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6'>
                            <h3 className='text-lg font-bold text-purple-900 mb-3'>How do I schedule an appointment?</h3>
                            <p className='text-gray-600'>You can schedule online through our form above, call us at (555) 123-CARE, or use our patient portal.</p>
                        </div>
                        <div className='bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6'>
                            <h3 className='text-lg font-bold text-purple-900 mb-3'>What should I bring to my appointment?</h3>
                            <p className='text-gray-600'>Please bring a valid ID, insurance card, list of current medications, and any relevant medical records.</p>
                        </div>
                        <div className='bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6'>
                            <h3 className='text-lg font-bold text-purple-900 mb-3'>Do you accept insurance?</h3>
                            <p className='text-gray-600'>We accept most major insurance plans. Please call us to verify your specific coverage.</p>
                        </div>
                        <div className='bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6'>
                            <h3 className='text-lg font-bold text-purple-900 mb-3'>How early should I arrive?</h3>
                            <p className='text-gray-600'>Please arrive 15 minutes early for check-in, especially if this is your first visit with us.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;